import React from 'react';
import { Box, Typography, Grid, Paper, Chip, Button, Alert, List, ListItem, ListItemText } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Companion apps live at sibling subdomains following the same
// <service>.<env>.vectros.ai convention as this portal: the portal is
// developer.<env>.vectros.ai, the admin console is admin.<env>, the data app is
// app.<env>. Derive their URLs by swapping the portal's first DNS label so the
// links are correct per-env (staging vs prod) with no extra build config; fall
// back to staging for localhost / any host that breaks the convention.
// Exported for unit testing.
export function companionApps(hostname) {
  const base =
    hostname && hostname.startsWith('developer.')
      ? hostname.slice('developer.'.length) // "staging.vectros.ai" | "vectros.ai"
      : 'staging.vectros.ai';
  return {
    admin: `https://admin.${base}`,
    data: `https://app.${base}`,
  };
}

const APPS = [
  {
    key: 'admin',
    name: 'Admin Console',
    plane: 'Control plane',
    icon: <AdminPanelSettingsIcon />,
    description:
      'Manage who and what can reach your data: members, scoped API keys, roles, access profiles, ' +
      'app-context provisioning, and account/MFA settings.',
    bullets: ['Invite & manage members', 'Mint and revoke scoped keys', 'Roles, access profiles & contexts'],
  },
  {
    key: 'data',
    name: 'Vectros App',
    plane: 'Data plane',
    icon: <AutoAwesomeIcon />,
    description:
      'Work with your data directly: browse, search, author, and reason over records, schemas, folders, ' +
      'and documents — scoped to one app context at a time.',
    bullets: ['Browse records, schemas & folders', 'Hybrid & semantic search', 'Ask questions with RAG'],
  },
];

function AppCard({ app, url }) {
  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ p: 1, bgcolor: '#EFF6FF', borderRadius: '6px', color: '#3B82F6', display: 'flex' }}>
          {app.icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {app.name}
          </Typography>
          <Chip size="small" label={app.plane} sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }} />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {app.description}
      </Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {app.bullets.map((b) => (
          <ListItem key={b} disableGutters sx={{ py: 0 }}>
            <ListItemText
              primary={`• ${b}`}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <Button
          variant="contained"
          endIcon={<OpenInNewIcon />}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open {app.name}
        </Button>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {url.replace(/^https:\/\//, '')}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function AppsPage() {
  const apps = companionApps(typeof window !== 'undefined' ? window.location.hostname : '');
  const urlByKey = { admin: apps.admin, data: apps.data };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Apps
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        The Vectros platform ships two companion web apps alongside this developer portal.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        These open in a new tab. Sign in with the <strong>same account</strong> you use here. (Each
        app prompts for login separately.)
      </Alert>

      <Grid container spacing={3}>
        {APPS.map((app) => (
          <Grid item xs={12} md={6} key={app.key}>
            <AppCard app={app} url={urlByKey[app.key]} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
